import clsx from 'clsx';
import { PagePreviewThumb } from './PagePreviewThumb';
import { clusterTint } from './clusterTints';
import { usePagePreviewHover } from '../../components/pagePreview/usePagePreviewHover';
import { splitCluster, type PreviewLayoutProps } from './previewHelpers';

// Stacked layout: the familiar one-cluster-per-row view. Each cluster is its own
// block with an id-badge heading and a wrapping grid of fixed-width thumbnails.
// When tint is on, the cluster block carries the highlight. content-visibility
// keeps a huge category cheap to render by skipping off-screen clusters.
export function PagePreviewsStacked({ pagesGrouped, category, tint, compact, expanded, onExpand, resize }: PreviewLayoutProps) {
  const { show, hide } = usePagePreviewHover();
  return (
    <>
      {pagesGrouped.map(([clusterId, clusterPages], index) => {
        const { shown, hidden } = splitCluster(clusterPages, clusterId, compact, expanded);
        const t = clusterTint(index);

        return (
          <div
            key={clusterId}
            data-cluster-id={clusterId}
            id={`cluster-${clusterId}`}
            className={clsx(
              'mb-6 rounded-xl p-3 [content-visibility:auto] [contain-intrinsic-size:auto_600px]',
              tint && t.bg,
            )}
          >
            <h3 className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2 text-gray-600 dark:border-zinc-700 dark:text-zinc-100">
              <span className={clsx('rounded-md px-2 py-0.5 text-[11px] font-bold text-white', t.chip)}>⬡ {clusterId}</span>
              <span className="text-sm">
                {clusterPages.length} {clusterPages.length === 1 ? 'page' : 'pages'}
              </span>
            </h3>

            <div className="flex flex-wrap gap-4">
              {shown.map((page) => (
                <div
                  key={page.id}
                  data-page-card
                  onMouseEnter={(e) => show(page, e.currentTarget.getBoundingClientRect())}
                  onMouseLeave={hide}
                  className="w-(--thumb-w) cursor-pointer"
                >
                  <PagePreviewThumb page={page} category={category} resize={resize} />
                </div>
              ))}

              {hidden > 0 && (
                <button type="button" onClick={() => onExpand(clusterId)} className="w-(--thumb-w) cursor-pointer">
                  <div className="flex aspect-3/4 items-center justify-center rounded-md border border-dashed border-gray-300 text-sm font-bold text-gray-500 dark:border-zinc-600 dark:text-zinc-400">
                    +{hidden}
                  </div>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
